import { FC, ReactNode, useState } from 'react';

import { ChevronUpSmallIcon } from '@/assets/icons';
import { Button, Tag } from '@/components/atoms';
import { cn } from '@/utils/tailwind';

import {
  Dropdown,
  DropdownMenuCheckbox,
  DropdownMenuClearSelection,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../Dropdown/Dropdown';

export type CategoryDropdownOption = {
  label: string;
  value: string;
  [key: string]: any;
};

type CategoryDropdownProps = {
  type?: 'single' | 'multiple';
  triggerLabel: string;
  triggerIcon?: ReactNode;
  triggerVariant?: 'default' | 'transparent';
  options: CategoryDropdownOption[];
  selectedOptions?: string[];
  onChange?: (newSelectedOptions: string[]) => void;
  onClickClearSeletion?: () => void;
};

export const CategoryDropdown: FC<CategoryDropdownProps> = ({
  type = 'single',
  triggerLabel,
  triggerIcon,
  triggerVariant,
  options,
  selectedOptions = [],
  onChange = () => {},
  onClickClearSeletion,
}) => {
  const Component = type === 'single' ? DropdownMenuItem : DropdownMenuCheckbox;

  const [isOpenDropdown, setIsOpenDropdown] = useState(false);

  const handleChangeOption = (category: CategoryDropdownOption) => {
    const isExist = selectedOptions.find(option => category.value === option);

    if (type === 'single') {
      onChange(isExist ? [] : [category.value]);
    } else {
      onChange(
        isExist
          ? selectedOptions.filter(option => option !== category.value)
          : [...selectedOptions, category.value]
      );
    }
  };

  const hasSelection = selectedOptions.length > 0;
  const hasOptions = options.length > 0;

  return (
    <Dropdown onOpenChange={setIsOpenDropdown}>
      <DropdownMenuTrigger variant={triggerVariant} asChild>
        <Button className="gap-2" variant="secondary">
          {triggerIcon}{' '}
          <span
            className={cn(
              'text-body-14-medium transition-colors mr-1',
              isOpenDropdown ? '!text-custom-warm-grey-700' : '!text-custom-warm-grey-500'
            )}
          >
            {triggerLabel}
          </span>{' '}
          {hasSelection ? (
            <Tag size="regular" color="cyan" label={selectedOptions.length.toString()} />
          ) : (
            <span className="text-custom-warm-grey-400">
              <ChevronUpSmallIcon
                className={cn('transition-transform', isOpenDropdown ? 'rotate-180' : 'rotate-0')}
              />
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      {hasOptions && (
        <DropdownMenuContent className="min-w-[360px]" align="start">
          {options.map(categoryOption => (
            <Component
              key={categoryOption.value}
              checked={Boolean(selectedOptions.find(option => categoryOption.value === option))}
              onCheckedChange={() => handleChangeOption(categoryOption)}
            >
              {categoryOption.label}
            </Component>
          ))}
          {hasSelection && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuClearSelection onClick={onClickClearSeletion}>
                Clear selection
              </DropdownMenuClearSelection>
            </>
          )}
        </DropdownMenuContent>
      )}
    </Dropdown>
  );
};
